/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { memo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import {
  Modal,
  ModalContent,
  ModalHeader,
  Button,
  Cross,
  InputField,
  RadioGroup,
  InputWithAction,
  Text,
} from '@cashfree-intl/coherent';
import { CURRENCY_SYMBOL } from 'utils/constants';
import { roundOffNumber } from 'utils/common';
import FormikComponent from '../../../components/Formik';
import ErrorMessage from '../../../components/ErrorMessage';

import useClickOutside from '../../../utils/hooks';
import { ADJUSTMENT_TYPE, VENDOR_TYPE, VENDOR_TYPE_PG } from '../constants';

const validationSchema = Yup.object({
  merchantVendorId: Yup.string()
    .required('This field cannot be left blank')
    .trim(),
  amount: Yup.number('Enter a valid amount')
    .positive('Enter a valid amount')
    .required('This field cannot be left blank')
    .typeError('Enter a valid amount'),
  type: Yup.string()
    .required('This field cannot be left blank')
    .max(50, 'More than 50 characters')
    .trim(),
});

const validate = vendorData => values => {
  const errors = {};
  if (
    values.amount &&
    vendorData &&
    values.type &&
    values.type === 'DEBIT' &&
    Number(values.amount) > vendorData.balance
  ) {
    errors.amount = `Amount should be less than equal to the unsettled balance (${
      CURRENCY_SYMBOL.INR
    } ${
      !vendorData.balance
        ? vendorData.balance.toFixed(2)
        : roundOffNumber(vendorData.balance)
    })`;
  }
  return errors;
};

export function AdjustBalanceModal({ onClose, onSubmit, vendorDataById }) {
  const [vendorData, setVendorData] = useState(null);

  const modalRef = useRef(null);
  useClickOutside(modalRef, onClose);

  const handleFormSubmit = (values, actions) => {
    const data = {
      ...values,
      productType: VENDOR_TYPE[vendorData.vendorType],
    };
    onSubmit(data, actions);
  };

  return (
    <Modal
      className="common-modal-wrapper adjust-balance-modal"
      ref={modalRef}
      open
    >
      <ModalHeader>
        Initiate Adjustment{' '}
        <Cross
          onClick={onClose}
          data-event-name="Form_AdjustBalanceModal_Icon_Close"
          data-ignore-inner-text="true"
        />
      </ModalHeader>
      <ModalContent>
        <div className="modal-content">
          <FormikComponent
            initialValues={{
              merchantVendorId: '',
              amount: '',
              type: ADJUSTMENT_TYPE[0].value,
              remarks: '',
            }}
            validationSchema={validationSchema}
            validate={validate(vendorData)}
            onSubmit={handleFormSubmit}
          >
            {({
              values,
              handleFieldBlur,
              handleFieldChange,
              handleSubmit,
              errors,
              touched,
              setFieldValue,
              ...formikProps
            }) => {
              const showError = !!errors.amount && !!touched.amount;
              const disableField =
                !formikProps.isValidating && formikProps.isSubmitting;

              const onVendorIdBlur = (e, data) => {
                if (e && e.target && e.target.value.length > 0) {
                  setVendorData('IN_REVIEW');
                  vendorDataById({
                    vendorId: e.target.value,
                    cb: response => {
                      if (response.merchantVendorId && response.status) {
                        // populating the vendorType if empty
                        if (!response.vendorType)
                          response.vendorType = VENDOR_TYPE_PG;

                        setVendorData(response);
                        if (errors.merchantVendorId) {
                          formikProps.setFieldError('merchantVendorId', '');
                        }
                      } else {
                        setVendorData(null);
                        formikProps.setFieldTouched('merchantVendorId', true);
                        formikProps.setFieldError(
                          'merchantVendorId',
                          'Please enter a valid Vendor Id',
                        );
                      }
                    },
                    errorCb: () => {
                      setVendorData(null);
                      formikProps.setFieldTouched('merchantVendorId', true);
                      formikProps.setFieldError(
                        'merchantVendorId',
                        'Please enter a valid Vendor Id',
                      );
                    },
                  });
                }
                handleFieldBlur(e, data);
              };

              const disableRestFields =
                !vendorData || vendorData === 'IN_REVIEW';

              const handleRadioOptions = obj => ({
                ...obj,
                checked: values.type === obj.value,
                onBlur: handleFieldBlur,
                onChange: handleFieldChange,
                disabled: disableRestFields || disableField,
              });

              const onKeyDown = e => {
                const asciiValue = e.key.charCodeAt(0);

                const arr = String(e.target.value).split('.');
                const decimalValues = arr.length > 1 ? arr.pop() : '';

                if (
                  ['e', 'E', '+', '-'].includes(e.key) ||
                  (asciiValue >= 48 &&
                    asciiValue <= 57 &&
                    decimalValues.length > 1)
                ) {
                  e.preventDefault();
                }
              };

              return (
                <form autoComplete="off" onSubmit={handleSubmit}>
                  <div className="form-input-wrapper vendor-id">
                    <InputField
                      fluid
                      id="merchantVendorId"
                      name="merchantVendorId"
                      label="Vendor Id"
                      value={values.merchantVendorId}
                      errors={errors}
                      touched={touched}
                      placeholder="Vendor Id"
                      onChange={handleFieldChange}
                      onBlur={onVendorIdBlur}
                      disabled={vendorData || disableField}
                    />
                  </div>
                  <div className="form-input-wrapper mt-3">
                    <RadioGroup
                      fieldLabel="Adjustment Type"
                      radioOptions={ADJUSTMENT_TYPE.map(handleRadioOptions)}
                    />
                  </div>
                  <ErrorMessage name="type" />
                  <div className="form-input-wrapper">
                    <div className="mt-1 mb-3">
                      <Text variant="b14" color="bodyLight" className="mb-1">
                        Transfer Amount
                      </Text>
                      <InputWithAction
                        fluid
                        className={` ${showError ? 'error_border' : ''}`}
                        id="amount"
                        name="amount"
                        label={{ basic: true, content: CURRENCY_SYMBOL.INR }}
                        // check if only numeric type is allowed
                        value={values.amount}
                        type="number"
                        onKeyDown={onKeyDown}
                        placeholder="Transfer Amount"
                        onChange={handleFieldChange}
                        onBlur={handleFieldBlur}
                        disabled={disableRestFields || disableField}
                      />
                    </div>
                    <ErrorMessage name="amount" className="mt-3" />
                  </div>
                  <div className="form-input-wrapper">
                    <Text variant="b14" color="bodyLight">
                      Remarks
                      <span className="optional-text mb-1">Optional</span>
                    </Text>
                    <InputField
                      fluid
                      id="remarks"
                      name="remarks"
                      value={values.remarks}
                      errors={errors}
                      touched={touched}
                      maxLength={50}
                      placeholder="Remarks"
                      onChange={handleFieldChange}
                      onBlur={handleFieldBlur}
                      disabled={disableRestFields || disableField}
                    />
                  </div>

                  {/* <div className="cta-top"> */}
                  <div className="cta-wrapper">
                    <Button
                      type="button"
                      link
                      className="link"
                      onClick={onClose}
                      disabled={disableField}
                      data-event-name="Form_AdjustBalanceModal_Button_Cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      primary
                      type="submit"
                      loading={disableField}
                      disabled={
                        !formikProps.isValid ||
                        disableField ||
                        vendorData === 'IN_REVIEW'
                      }
                      data-event-name="Primary_Button_Ok"
                    >
                      Ok
                    </Button>
                  </div>
                  {/* </div> */}
                </form>
              );
            }}
          </FormikComponent>
        </div>
      </ModalContent>
    </Modal>
  );
}

AdjustBalanceModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  vendorDataById: PropTypes.func.isRequired,
};

export default memo(AdjustBalanceModal);
